<template>
    <div class="business-permit-container">
        <!-- 1. Clean Preview Section -->
        <div class="preview-section">
            <div class="preview-controls">
                <div class="btn-group">
                    <!-- <button @click="toggleViewMode('preview')" 
                            :class="['btn', viewMode === 'preview' ? 'btn-primary' : 'btn-outline-primary']">
                        <i class="fas fa-eye me-1"></i> Preview
                    </button> -->
                    <button @click="toggleViewMode('print')"
                        :class="['btn', viewMode === 'print' ? 'btn-primary' : 'btn-outline-primary']">
                        <i class="fas fa-print me-1"></i> Print View
                    </button>
                </div>

                <div class="btn-group">
                    <button @click="downloadAsImage" class="btn btn-success">
                        <i class="fas fa-download me-1"></i> Download Image
                    </button>
                    <button @click="downloadAsPDF" class="btn btn-info">
                        <i class="fas fa-file-pdf me-1"></i> Download PDF
                    </button>
                    <button @click="printDirect" class="btn btn-warning">
                        <i class="fas fa-print me-1"></i> Print Now
                    </button>
                </div>
            </div>

            <!-- Preview Card -->
            <div v-show="viewMode === 'preview'" class="preview-card">
                <div class="preview-header">
                    <h4>Business Permit Preview</h4>
                    <small class="text-muted">Certificate No: {{ permitYear }} - {{ formattedSeries }}</small>
                </div>

                <div class="preview-content">
                    <!-- Business Info -->
                    <div class="info-section">
                        <div class="info-row">
                            <span class="info-label">Business Name:</span>
                            <span class="info-value">{{ businessName }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Owner:</span>
                            <span class="info-value">{{ ownerFullName }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">B.I.N:</span>
                            <span class="info-value">{{ currentApplication?.application?.bin }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Address:</span>
                            <span class="info-value">{{ businessAddress }}</span>
                        </div>
                    </div>

                    <!-- Validity -->
                    <div class="validity-section">
                        <div class="validity-card issued">
                            <div class="validity-label">Date Issued</div>
                            <div class="validity-value">{{ formatDate(dateIssued) }}</div>
                        </div>
                        <div class="validity-card expires">
                            <div class="validity-label">Valid Until</div>
                            <div class="validity-value">{{ formatDate(dateExpired) }}</div>
                        </div>
                        <div class="validity-card fee">
                            <div class="validity-label">Amount Paid</div>
                            <div class="validity-value">{{ formattedAmount }}</div>
                        </div>
                    </div>

                    <!-- QR Code Preview -->
                    <div class="qrcode-preview">
                        <div class="qrcode-container">
                            <canvas ref="previewQrCode" class="qrcode-canvas"></canvas>
                        </div>
                        <div class="qrcode-info">
                            <p class="mb-1">Scan to verify permit authenticity</p>
                            <small class="text-muted">Valid in {{ permitYear }}</small>
                        </div>
                    </div>

                    <!-- Status Badge -->
                    <div class="status-badge">
                        <span class="badge bg-success">
                            <i class="fas fa-check-circle me-1"></i>
                            ACTIVE PERMIT
                        </span>
                    </div>
                </div>
            </div>

            <!-- Printable Version (Hidden) -->
            <div v-show="viewMode === 'print'" class="print-view-container">
                <div class="print-controls">
                    <div class="form-check">
                        <input type="checkbox" class="form-check-input" id="showWatermark" v-model="showWatermark">
                        <label class="form-check-label" for="showWatermark">Show Draft Watermark</label>
                    </div>
                </div>

                <div class="printable-area">

                    <!-- Printable Permit (Hidden during preview) -->
                    <div ref="printablePermit" class="printable-permit" :class="{ 'draft-watermark': showWatermark }">
                        <div class="permit-content">
                            <!-- QR Code will be inserted here -->
                            <TheSignature font-size="40px" imageOffsetY="-120"  max-img-height="200px" :position="JSON.parse(sig.signature_position_json)"
                                :sig="sig" v-for="sig in signatories" :key="`sig${sig.official_id}`" />
                            <!-- Certificate Number -->
                            <h2 class="certificate-number">
                                {{ permitYear }} - {{ formattedSeries }}
                            </h2>

                            <!-- First Table -->
                            <table class="info-table table-1">
                                <tbody>
                                    <tr>
                                        <td>
                                            B.I.N <br>
                                            <b>{{ currentApplication?.application?.bin }}</b>
                                        </td>
                                        <td>
                                            Application Type <br>
                                            <b>{{ currentApplication?.permitInfo?.type }}</b>
                                        </td>
                                        <td>
                                            O.R No. <br>
                                            <b>{{ orNo }}</b>
                                        </td>
                                        <td>
                                            Amount Paid <br>
                                            <b>{{ formattedAmount }}</b>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Date Issued <br>
                                            <b>{{ formatDate(dateIssued) }}</b>
                                        </td>
                                        <td>
                                            Date Expires <br>
                                            <b>{{ formatDate(dateExpired) }}</b>
                                        </td>
                                        <td>
                                            Period <br>
                                            <b>{{ paymentMode }} - {{ permitYear }}</b>
                                        </td>
                                        <td>
                                            Payment Date <br>
                                            <b>{{ formatDate(paymentDate) }}</b>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Certifies that text -->
                            <h6 class="certifies-text">
                                This Certifies that
                            </h6>

                            <!-- Business Info Table -->
                            <table class="info-table business-info">
                                <tbody>
                                    <tr>
                                        <td>
                                            <b>{{ businessName }}</b>
                                            <br>
                                            <span>Business Name</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <b>{{ ownerFullName }}</b> <br>
                                            <span>Owner's Name</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <b>{{ businessAddress }}</b> <br>
                                            <span>Business Address</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Description Text -->
                            <div class="description-text">
                                <span>
                                    <b>
                                        Has been granted Permit to operate the following Business/es puruant to the
                                        REVENUE
                                        CODE<br> of the
                                        Municipality of Palompon and after payment of taxes, fees and other
                                        regulatory<br>
                                        charges and subject to the
                                        compliance of such other pertinent laws, ordinances and related
                                        <br />administrative
                                        regulations.
                                    </b>
                                </span>
                            </div>

                            <!-- Line of Business Table -->
                            <table class="info-table lob-table">
                                <tbody>
                                    <tr>
                                        <td>Kind of Business</td>
                                        <td>Permit Fees</td>
                                        <td>Remarks</td>
                                    </tr>
                                    <tr v-for="(lob, index) in lineOfBusiness" :key="index">
                                        <td>{{ lob.line_of_business }}</td>
                                        <td>
                                            <span v-if="index === 0">{{ formattedAmount }}</span>
                                        </td>
                                        <td><br></td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Remarks Section -->
                            <div class="remarks-section">
                                <span>Remarks:</span>
                                <ul>
                                    <li>NON-TRANSFERABLE AND VALID ONLY WITH CORRESPONDING OFFICIAL RECEIPTS SHOWING
                                        PAYMENT FEES <br>
                                        AND MUNICIPALITY TAXES, ERASURES/ALTERATIONS WILL INVALIDATE THIS PERMIT.
                                    </li>
                                    <li>
                                        FAILURE TO RENEW THIS BUSINESS PERMIT/LICENSE WITHIN THE PRESCRIBED PERIOD SHALL
                                        SUBJECT THE <br>
                                        TAXPAYER TO TWENTY-FIVE PERCENT (25%) SURCHARGE OF THE PERMIT FEE.
                                    </li>
                                    <li>
                                        THIS PERMIT SHALL BE POSTED CONSPICUOUSLY AT THE PLACE OF BUSINESS AND SHALL BE
                                        PRESENTED <br>
                                        AND/OR SURRENDERED TO CONCERNED AUTHORITIES UPON DEMAND.
                                    </li>
                                    <li>
                                        SURRENDER THIS PERMIT WITHIN 20 DAYS UPON CLOSURE OF BUSINESS TO AVOID
                                        PENALTY.
                                    </li>
                                    <li>
                                        SUBJECT TO COMPLIANCE AND INSPECTION TO ORDINANCES RELATED TO CONDUCT OF
                                        BUSINESS.
                                    </li>
                                </ul>
                            </div>

                            <!-- Signature -->
                            <div class="signature-section">

                                <h5 class="signatory-details">
                                    <span>Digitally Signed</span>
                                </h5>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import jsPDF from 'jspdf'; // You'll need to install: npm install jspdf
import spinnerService from '@/services/spinnerService';
import TheSignature from '../docs/TheSignature.vue';
import signatureService from '@/services/signatureService';
/* eslint-disable */
// Props
const props = defineProps({
    currentApplication: {
        type: Object,
        default: () => ({})
    },
    userSignatures: {
        type: Object,
        default: () => ({})
    }
});

// Refs
const printablePermit = ref(null);
const previewQrCode = ref(null);
const viewMode = ref('print'); // 'preview' or 'print'
const showWatermark = ref(false);
const generatedImage = ref(null);
const signatories = ref([]);
const ownerFullName = computed(() => {
    const app = props.currentApplication;
    const firstName = app.permitInfo?.owner_firstname || '';
    const middleName = app.permitInfo?.owner_middlename || '';
    const lastName = app.permitInfo?.owner_lastname || '';

    // Format: FirstName [MiddleInitial.] LastName
    let fullName = firstName.trim();

    // Add middle initial if middle name exists and is not empty/null/NA
    if (middleName &&
        middleName.trim() !== '' &&
        middleName.trim().toLowerCase() !== 'n/a' &&
        middleName.trim().toLowerCase() !== 'null' &&
        middleName.trim().toLowerCase() !== 'none') {

        // Take first character of middle name and add period
        const middleInitial = middleName.trim().charAt(0).toUpperCase() + '.';
        fullName += ' ' + middleInitial;
    }

    // Add last name
    fullName += ' ' + lastName.trim();

    return fullName.trim();
});

const businessName = computed(() => {
    return props.currentApplication?.permitInfo?.business_name || 'N/A';
});

const businessAddress = computed(() => {
    const app = props.currentApplication;
    const parts = [
        app.businessInfo?.operation_building_name,
        app.businessInfo?.operation_block_no_lot_no,
        app.businessInfo?.operation_street,
        app.businessInfo?.operation_subdivision,
        app.businessInfo?.operation_barangay,
        app.businessInfo?.operation_city,
        app.businessInfo?.operation_province
    ].filter(part => part && part !== 'None' && part !== 'N/A' && part !== 'null');
    return parts.join(' ') || 'N/A';
});

const lineOfBusiness = computed(() => {
    return props.currentApplication?.permitLineOfBusinesses || [];
});

const permitYear = computed(() => {
    return props.currentApplication?.permitData?.year || new Date().getFullYear();
});

const formattedSeries = computed(() => {
    const series = props.currentApplication?.businessPermitData?.series || '0000';
    return series.toString().padStart(4, '0');
});

const orNo = computed(() => {
    return props.currentApplication?.paymentHistory?.or_no || 'N/A';
});

const dateIssued = computed(() => {
    return props.currentApplication?.businessPermitData?.date_issued || '';
});

const dateExpired = computed(() => {
    return props.currentApplication?.businessPermitData?.date_expired || '';
});

const paymentMode = computed(() => {
    return props.currentApplication?.permitInfo?.mode_of_payment || 'ANNUAL';
});

const paymentDate = computed(() => {
    return props.currentApplication?.paymentHistory?.ph_date_created || '';
});

const formattedAmount = computed(() => {
    const amount = props.currentApplication?.paymentHistory?.trxn_amount;
    if (amount) {
        const num = parseFloat(amount);
        if (!isNaN(num)) {
            return new Intl.NumberFormat('en-PH', {
                style: 'currency',
                currency: 'PHP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(num);
        }
    }
    return '₱0.00';
});

const mayorSignature = computed(() => {
    const sig = props.userSignatures?.Mayor;
    if (sig?.ds_id && sig?.type) {
        return `ezgovtEngine/uploads/setup/signature/${sig.ds_id}/${sig.type}-sig.png`;
    }
    return null;
});

const mayorSignatoryName = computed(() => {
    return props.userSignatures?.Mayor?.signatory_name || 'Municipal Mayor';
});

// Methods
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const [month, day, year] = parts;
                return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
            }
            return dateString;
        }

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    } catch (error) {
        return dateString;
    }
};

const toggleViewMode = (mode) => {
    viewMode.value = mode;
    if (mode === 'print') {
        // Generate QR code for printable version
        setTimeout(() => generatePrintableQRCode(), 100);
    }
};

// Generate QR Code for preview
const generatePreviewQRCode = async () => {
    try {
        const qrData = {
            type: 'BUSINESS_PERMIT',
            bin: props.currentApplication?.application?.bin || '',
            permitNumber: `${permitYear.value}-${formattedSeries.value}`,
            businessName: businessName.value,
            ownerName: ownerFullName.value,
            issueDate: dateIssued.value,
            expiryDate: dateExpired.value,
            status: 'VALID'
        };

        if (previewQrCode.value) {
            await QRCode.toCanvas(previewQrCode.value, JSON.stringify(qrData), {
                width: 120,
                margin: 1,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                },
                errorCorrectionLevel: 'H'
            });
        }
    } catch (error) {
        console.error('Error generating preview QR code:', error);
    }
};

// Generate QR Code for printable version
const generatePrintableQRCode = async () => {
    try {
        const qrData = {
            type: 'BUSINESS_PERMIT',
            bin: props.currentApplication?.application?.bin || '',
            permitNumber: `${permitYear.value}-${formattedSeries.value}`,
            businessName: businessName.value,
            ownerName: ownerFullName.value,
            issueDate: dateIssued.value,
            expiryDate: dateExpired.value,
            status: 'VALID'
        };

        // Find or create QR code canvas in printable permit
        let qrCanvas = document.querySelector('#printable-permit-qrcode');
        if (!qrCanvas) {
            qrCanvas = document.createElement('canvas');
            qrCanvas.id = 'printable-permit-qrcode';
            qrCanvas.style.cssText = 'z-index: 1000; position: absolute; top: 900px; left: 350px; width: 280px; height: 280px;';

            const permitContainer = printablePermit.value?.querySelector('.permit-content');
            if (permitContainer) {
                permitContainer.prepend(qrCanvas);
            }
        }

        await QRCode.toCanvas(qrCanvas, JSON.stringify(qrData), {
            width: 280,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            },
            errorCorrectionLevel: 'H'
        });
    } catch (error) {
        console.error('Error generating printable QR code:', error);
    }
};

// Generate high-quality image for download
const generatePermitImage = async () => {
    if (!printablePermit.value) return null;

    try {
        spinnerService.show();
        // Generate QR code first
        await generatePrintableQRCode();

        // Create canvas with high resolution
        const canvas = await html2canvas(printablePermit.value, {
            scale: 3, // Higher resolution for download
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
            removeContainer: true,
            width: 2480,
            height: 3508,
            windowWidth: 2480,
            windowHeight: 3508
        });

        // Convert to data URL
        const imageData = canvas.toDataURL('image/png', 1.0);
        generatedImage.value = imageData;

        return imageData;
    } catch (error) {
        console.error('Error generating permit image:', error);
        throw error;
    }
    finally {
        spinnerService.hide();
    }
};

// Download as PNG image
const downloadAsImage = async () => {
    try {
        spinnerService.show();
        const imageData = await generatePermitImage();
        if (!imageData) return;

        // Create download link
        const link = document.createElement('a');
        const fileName = `Business_Permit_${permitYear.value}_${formattedSeries.value}.png`;
        link.href = imageData;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        alert('Failed to download image. Please try again.');
        console.error(error);
    }
    finally {
        spinnerService.hide();
    }
};

// Download as PDF
const downloadAsPDF = async () => {
    try {

        const imageData = await generatePermitImage();
        if (!imageData) return;

        // Create PDF in A4 format (portrait)
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        // Add image to PDF
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // Calculate dimensions to fit page
        const imgProps = pdf.getImageProperties(imageData);
        const imgWidth = pdfWidth;
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

        // Add image
        pdf.addImage(imageData, 'PNG', 0, 0, imgWidth, imgHeight);

        // Save PDF
        const fileName = `Business_Permit_${permitYear.value}_${formattedSeries.value}.pdf`;
        pdf.save(fileName);
    } catch (error) {
        alert('Failed to generate PDF. Please try again.');
        console.error(error);
    }
};

// Direct print
const printDirect = async () => {
    try {
        await generatePermitImage();

        // Open image in new window and print
        const printWindow = window.open('');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Print Business Permit</title>
                    <style>
                        body { margin: 0; }
                        img { 
                            width: 100%; 
                            height: auto;
                            page-break-inside: avoid;
                        }
                        @media print {
                            body { margin: 0; }
                            img { max-width: 100% !important; }
                        }
                    </style>
                </head>
                <body>
                    <img src="${generatedImage.value}" onload="window.print(); setTimeout(() => window.close(), 1000);">
                </body>
            </html>
        `);
        printWindow.document.close();
    } catch (error) {
        alert('Failed to print. Please try again.');
        console.error(error);
    }
};

const getSignatories = async (code) => {
    signatories.value = await signatureService.getSignatoriesByCode(code);
}
defineExpose({ getSignatories });

// Initialize
onMounted(() => {
    generatePreviewQRCode();
});
</script>

<style scoped>
.business-permit-container {
    padding: 20px;
    background: #f8f9fa;
    min-height: 100vh;
}

/* Preview Controls */
.preview-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 15px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-group {
    display: flex;
    gap: 10px;
}

/* Preview Card */
.preview-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.preview-header {
    background: linear-gradient(135deg, #1a237e, #283593);
    color: white;
    padding: 20px;
    text-align: center;
}

.preview-header h4 {
    margin: 0;
    font-weight: 600;
}

.preview-content {
    padding: 30px;
}

/* Info Sections */
.info-section {
    margin-bottom: 30px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
}

.info-row {
    display: flex;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #dee2e6;
}

.info-row:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
}

.info-label {
    font-weight: 600;
    color: #495057;
    min-width: 120px;
}

.info-value {
    color: #212529;
    flex: 1;
}

/* Validity Section */
.validity-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-bottom: 30px;
}

.validity-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-top: 4px solid;
}

.validity-card.issued {
    border-color: #28a745;
}

.validity-card.expires {
    border-color: #dc3545;
}

.validity-card.fee {
    border-color: #007bff;
}

.validity-label {
    font-size: 12px;
    color: #6c757d;
    text-transform: uppercase;
    margin-bottom: 8px;
}

.validity-value {
    font-size: 18px;
    font-weight: 600;
    color: #212529;
}

/* QR Code Preview */
.qrcode-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin: 30px 0;
    padding: 20px;
    background: white;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}

.qrcode-container {
    padding: 10px;
    background: white;
    border-radius: 4px;
    border: 1px solid #dee2e6;
}

.qrcode-info {
    text-align: left;
}

/* Status Badge */
.status-badge {
    text-align: center;
    margin-top: 20px;
}

.status-badge .badge {
    font-size: 14px;
    padding: 10px 20px;
    border-radius: 50px;
}

/* Print View */
.print-view-container {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.print-controls {
    margin-bottom: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 6px;
}

.printable-area {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    overflow: auto;
    max-height: 70vh;
    background: #fafafa;
}

/* Printable Permit */
.printable-permit {
    background: url('@/assets/img-headers/bpermit.jpg') no-repeat;
    background-size: 2480px 3508px;
    position: relative;
    width: 2480px;
    height: 3508px;
    margin: 0 auto;
    font-family: 'Times New Roman', Times, serif;
    transform-origin: top left;
}

.permit-content {
    position: relative;
    width: 100%;
    height: 100%;
}

/* Draft Watermark */
.draft-watermark::before {
    content: "DRAFT PREVIEW";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 120px;
    color: rgba(255, 0, 0, 0.1);
    font-weight: bold;
    z-index: 9999;
    pointer-events: none;
    white-space: nowrap;
}

/* Original positioning styles (same as your original) */
.certificate-number {
    z-index: 1000;
    position: absolute;
    top: 1010px;
    left: 960px;
    font-size: 95px;
    margin: 0;
    color: #000;
}

.info-table {
    z-index: 1000;
    position: absolute;
    border: 1px solid black;
    border-collapse: collapse;
    background: rgba(255, 255, 255, 0.95);
}

.table-1 {
    top: 1200px;
    left: 145px;
    width: 88%;
    font-size: 50px;
}

.table-1 td {
    border: 1px solid black;
    text-align: center;
    vertical-align: middle;
}

.certifies-text {
    z-index: 1000;
    position: absolute;
    top: 1520px;
    left: 1090px;
    color: darkred;
    font-size: 50px;
    margin: 0;
}

.business-info {
    top: 1590px;
    left: 145px;
    width: 88%;
    font-size: 37px;
}

.business-info td {
    border: 1px solid black;
    text-align: center;
    background: transparent !important;
    vertical-align: middle;
}

.business-info span {
    font-size: 30px;
    color: #666;
    display: block;
    margin-top: 10px;
}

.description-text {
    z-index: 1000;
    position: absolute;
    top: 2040px;
    left: 145px;
    color: black;
    font-size: 32px;
    width: 88%;
    line-height: 1.4;
}

.lob-table {
    top: 2230px;
    left: 145px;
    width: 88%;
    border: 1px solid black;
    font-size: 50px;
}

.lob-table td {
    border: 1px solid black;
    color: black;
    text-align: center;
    vertical-align: middle;
}

.remarks-section {
    z-index: 1000;
    position: absolute;
    top: 2650px;
    left: 145px;
    color: black;
    font-size: 30px;
    width: 88%;
    text-align: left;
}

.remarks-section span {
    font-weight: bold;
    font-size: 32px;
    display: block;
    margin-bottom: 20px;
}

.remarks-section ul {
    margin: 20px 0 0 40px;
    line-height: 1.6;
}

.remarks-section li {
    margin-bottom: 20px;
}

.signature-section {
    z-index: 1000;
    position: absolute;
    top: 2950px;
    left: 1020px;
    color: black;
}

.signature-image {
    width: 400px;
    height: auto;
    display: block;
}

.signatory-details {
    position: absolute;
    top: 190px;
    left: -70px;
    width: 540px;
    color: black;
    font-size: 45px;
    text-align: center;
    margin: 0;
}

.signatory-details div {
    font-size: 20px;
    margin-top: 5px;
    font-weight: normal;
}

.signatory-details span {
    font-size: 10px;
    color: #666;
    font-style: italic;
    display: block;
    margin-top: 5px;
}

/* Responsive Design */
@media (max-width: 768px) {
    .preview-controls {
        flex-direction: column;
        gap: 15px;
    }

    .validity-section {
        grid-template-columns: 1fr;
    }

    .qrcode-preview {
        flex-direction: column;
        text-align: center;
    }

    .printable-area {
        max-height: 50vh;
    }
}

/* Print-specific styles */
@media print {
    .business-permit-container {
        padding: 0;
        background: white;
    }

    .preview-controls,
    .preview-card,
    .print-controls {
        display: none !important;
    }

    .printable-area {
        max-height: none;
        overflow: visible;
        border: none;
    }

    .printable-permit {
        transform: none;
        width: 100% !important;
        height: 100% !important;
    }
}
</style>